import { ElementClassManager as ElementClassManagerClass } from './ElementClassManager';

export const Modal = {
    show: function (ref: any) {
        ref.modal('show');
    },
    hide: function (ref: any) {
        ref.modal('hide');
    }
};

export const DownloadFile = {
    downloadFromUrl: function (url: string, fileName?: string): boolean {
        let error = false;
        try {
            const anchorElement = document.createElement('a');
            anchorElement.href = url;
            anchorElement.target = "_blank";
            anchorElement.download = fileName ?? '';
            anchorElement.click();
            anchorElement.remove();
        }
        catch (ex) {
            error = true;
        }
        return error;
    },
    downloadFromByteArray: function (filename: string, contentType: string, content: any): boolean {
        let error = false;
        try {
            // Create the URL
            const file = new File([content], filename, { type: contentType });
            const exportUrl = URL.createObjectURL(file);

            // Create the <a> element and click on it
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.href = exportUrl;
            a.download = filename;
            a.target = "_self";
            a.click();

            // We don't need to keep the url, let's release the memory
            // On Safari it seems you need to comment this line... (please let me know if you know why)
            URL.revokeObjectURL(exportUrl);
        }
        catch (ex) {
            error = true;
        }
        return error;
    },
    /**
     * Hands a same-origin URL to the browser's own download manager and returns immediately, then
     * watches for the server's completion cookie and reports back through `callback`.
     *
     * Returning immediately is the point. downloadViaFetch awaited the whole transfer inside the
     * interop call, and JSInteropDefaultCallTimeout is one minute, so every download slower than
     * that died with a TaskCanceledException. It also buffered the file into a Blob, so browser
     * memory scaled with the export.
     *
     * But an anchor click gives the page no completion event at all, which left the spinner dead
     * while the server was still working. The fix is the classic download-token cookie: the URL
     * carries a token, the server sets a readable cookie of the same name at the moment it starts
     * sending, and this poll turns that into the callback the C# side awaits. `1` means the file is
     * on its way, `0` means the server refused. A timeout means neither ever happened.
     *
     * The server must send Content-Disposition: attachment — `download` is only a hint — and must
     * mark the cookie non-HttpOnly and essential, or it is never visible here.
     */
    startDownload: function (url: string, fileName: string, cookieName: string, callback: any, timeoutSeconds: number): boolean {
        try {
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            a.target = "_self";
            a.rel = "noopener";
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            a.remove();
        }
        catch (ex) {
            console.error("startDownload could not start the download", url, ex);
            return true;
        }

        const readCookie = (): string | null => {
            const prefix = `${cookieName}=`;
            const match = document.cookie.split("; ").find(c => c.startsWith(prefix));
            return match ? match.substring(prefix.length) : null;
        };

        const deadline = Date.now() + timeoutSeconds * 1000;
        const poll = window.setInterval(() => {
            const value = readCookie();
            if (value === null && Date.now() < deadline) {
                return;
            }

            window.clearInterval(poll);
            if (value !== null) {
                // Consumed: a stale cookie would resolve the next export before it even started.
                document.cookie = `${cookieName}=; path=/; max-age=0`;
            }
            else {
                console.error("startDownload timed out waiting for the server", url);
            }

            // A throttled hidden tab can fire this after the C# side gave up and disposed the
            // reference, which rejects. The page already knows the outcome by then.
            callback.invokeMethodAsync("DownloadStarted", value === "1").catch(() => { });
        }, 400);

        return false;
    },
    downloadViaFetch: async function (url: string, fileName: string): Promise<boolean> {
        let error = false;
        try {
            const response = await fetch(url);
            if (!response.ok) {
                return true;
            }
            const blob = await response.blob();
            const exportUrl = URL.createObjectURL(blob);

            const a = document.createElement("a");
            document.body.appendChild(a);
            a.href = exportUrl;
            a.download = fileName;
            a.target = "_self";
            a.click();

            URL.revokeObjectURL(exportUrl);
            a.remove();
        }
        catch (ex) {
            error = true;
        }
        return error;
    },
};

export const Locale = {
    getLanguage() {
        return navigator.language || (navigator.languages && navigator.languages.length && navigator.languages[0]) || 'en'
    },

    getShort(language: any) {
        if (language.includes('nb')) return 'nb';
        if (language.includes('no')) return 'nb';
        if (language.includes('sv')) return 'sv';
        if (language.includes('da')) return 'da';
        if (language.includes('fi')) return 'fi';
        if (language.includes('ru')) return 'ru';
        return "default";
    },

    getBigNumberPrefixes: function (customLanguage: any) {
        let language = customLanguage ?? this.getShort(this.getLanguage());
        switch (language) {
            case 'nn':
            case 'nb':
            case 'no':
                return { billion: 'mrd.', million: 'mill.', thousand: 'T' };
            case 'sv':
                return { billion: 'mdr', million: 'm', thousand: 'T' };
            case 'da':
                return { billion: 'mia.', million: 'mio.', thousand: 'T' };
            case 'fi':
                return { billion: 'mrd.', million: 'milj.', thousand: 'T' };
            case 'ru':
                return { billion: 'млрд', million: 'млн', thousand: 'тыс' };
            default:
                return { billion: 'B', million: 'M', thousand: 'T' };
        }
    }
};

export const Clipboard = {
    copyText: async function (text: string): Promise<boolean> {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        }
        catch (err) {
            console.error('Failed to copy: ', err);
            return false;
        }
    }
};

export const Zoom = {
    moveDomElement: function (element: any, targetId: any) {
        const target = document.getElementById(targetId);
        if (element && target) {
            target.appendChild(element);
        }
    }
};

// Blazor only revives { "__internalId": <string> } into an element, so a default
// ElementReference arrives as a plain object and a stale one as null. Both entry
// points below reject that; this helper only ever sees a real element.
const cardContentHeight = (element: HTMLElement, selector: string): number | null => {
    const cardContent = element.closest('.mud-card')?.querySelector<HTMLElement>(selector);

    return cardContent ? cardContent.clientHeight : null;
};

export const TextTruncation = {
    getHeights: function (element: unknown): Promise<{ scrollHeight: number, clientHeight: number, parentCardHeight: number } | null> {
        if (!(element instanceof HTMLElement))
            return Promise.resolve(null);

        return new Promise((resolve, reject) => {
            requestAnimationFrame(() => {
                // An exception raised here escapes the promise, so it has to reach reject
                // explicitly or the awaiting .NET call never settles.
                try {
                    resolve({
                        scrollHeight: element.scrollHeight,
                        clientHeight: element.clientHeight,
                        parentCardHeight: cardContentHeight(element, '.mud-card-content.card-body') ?? 0
                    });
                }
                catch (err) {
                    reject(err);
                }
            });
        });
    },
    isDesktop: function (): boolean {
        return window.innerWidth >= 960;
    },
    getParentCardHeight: function (element: unknown): number | null {
        return element instanceof HTMLElement
            ? cardContentHeight(element, '.mud-card-content')
            : null;
    }
};

export const ElementClassManager = new ElementClassManagerClass();
